import type { ReactNode } from "react";
import {
  GridList as ReactAriaGridList,
  GridListItem as ReactAriaGridListItem,
} from "react-aria-components";
import type {
  GridListItemProps as ReactAriaGridListItemProps,
  GridListProps as ReactAriaGridListProps,
  Selection,
} from "react-aria-components";

import { cn, type VariantProps } from "../utils";
import { NcIconTick } from "./nc-icons";
import { ncListBoxClasses } from "./nc-list-box";

export type NcGridListSelection = Selection;
export type NcGridListProps<T extends object> = ReactAriaGridListProps<T>;

const NcGridList = function <T extends object>({ ...props }: NcGridListProps<T>) {
  return (
    <ReactAriaGridList
      data-nc="NcGridList"
      className={ncListBoxClasses.list({ variant: "default" })}
      {...props}
    />
  );
};

export type GridListItem = {
  id: number | string;
} & Record<string, string | number>;

export interface GridListItemProps
  extends ReactAriaGridListItemProps<GridListItem>, VariantProps<typeof ncListBoxClasses.item> {
  children: ReactNode;
}

const GridListItem = ({
  children,
  variant = "default",
  textValue,
  className,
  ...props
}: GridListItemProps) => {
  const childTextValue = typeof children === "string" ? children : undefined;
  return (
    <ReactAriaGridListItem
      data-nc="NcGridList.Item"
      className={cn("group", ncListBoxClasses.item({ variant }), className)}
      textValue={textValue || childTextValue}
      {...props}
    >
      <div className="border-ui hidden h-[1.2em] rounded border group-data-selection-mode:block">
        <NcIconTick className="invisible block text-current group-[[aria-selected='true']]:visible" />
      </div>
      <>{children}</>
    </ReactAriaGridListItem>
  );
};

NcGridList.Item = GridListItem;

export { NcGridList };
